import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Leaf } from 'lucide-react';
import { toast } from 'sonner';

export default function AuthPage({ isAdmin = false }) {
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginWithEmail, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await loginWithEmail(loginEmail, loginPassword);
      toast.success('Welcome back!');
      navigate(res?.user?.role === 'admin' ? '/admin' : '/', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await register(regName, regEmail, regPassword);
      if (res?.message) {
        toast.info(res.message);
      } else {
        toast.success('Account created!');
        navigate('/', { replace: true });
      }
    } catch (err) {
      toast.error(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    try {
      await loginWithGoogle();
    } catch (err) {
      toast.error(err.message || 'Google login failed');
    }
  };

  return (
    <div data-testid="auth-page" className={`min-h-screen flex items-center justify-center px-4 ${isAdmin ? 'bg-muted/30' : 'bg-background'}`}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Leaf className="h-7 w-7 text-primary" />
            <span className="text-2xl font-serif font-semibold">{isAdmin ? 'Admin Login' : 'Orgaanix'}</span>
          </div>
          <p className="text-muted-foreground text-sm">{isAdmin ? 'Sign in to manage your marketplace' : 'Fresh organic produce, farm to your table'}</p>
        </div>

        <div className="bg-card rounded-3xl border border-border p-8 shadow-sm">
          <Tabs defaultValue="login">
            <TabsList className="grid w-full grid-cols-2 mb-6 rounded-full">
              <TabsTrigger data-testid="auth-login-tab" value="login" className="rounded-full">Sign In</TabsTrigger>
              <TabsTrigger data-testid="auth-register-tab" value="register" className="rounded-full">Register</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input data-testid="login-email-input" id="login-email" type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} className="mt-1.5 rounded-2xl" />
                </div>
                <div>
                  <Label htmlFor="login-password">Password</Label>
                  <Input data-testid="login-password-input" id="login-password" type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} className="mt-1.5 rounded-2xl" />
                </div>
                <Button data-testid="login-submit-button" type="submit" className="w-full rounded-full" disabled={loading}>
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">or</span></div>
              </div>
              <Button data-testid="google-login-button" variant="outline" className="w-full rounded-full" onClick={handleGoogle}>
                <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Continue with Google
              </Button>
            </TabsContent>
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label>Full Name</Label>
                  <Input data-testid="register-name-input" value={regName} onChange={(e) => setRegName(e.target.value)} className="mt-1.5 rounded-2xl" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input data-testid="register-email-input" type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} className="mt-1.5 rounded-2xl" />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input data-testid="register-password-input" type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} className="mt-1.5 rounded-2xl" />
                </div>
                <Button data-testid="register-submit-button" type="submit" className="w-full rounded-full" disabled={loading}>
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">or</span></div>
              </div>
              <Button variant="outline" className="w-full rounded-full" onClick={handleGoogle}>
                <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Continue with Google
              </Button>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
